import download from 'downloadjs';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Apis from '../../../api';
import { HEADERMENU_PATH } from '../../../routes/RoutesURL';
import { RootState } from '../../../store/rootReducer';
import { DataRoom, DataRoomChildren } from '../../main/deals/redux/types';
import { PDFStateData } from '../../main/PDFView/PDFView';
import MessageActions from '../../message/redux/actions';
import BreadCrumbs from './BreadCrumbs';
import TreeView from './List';

type Props = {
    dataFiles: DataRoom[];
    isDeal: boolean;
    stateData?: PDFStateData;
    setInvestmentDoc?: (s?: string) => void;
};

const FolderStructure: React.FC<Props> = ({ dataFiles, isDeal, stateData, setInvestmentDoc }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const investorUUID = useSelector((state: RootState) => state.user.uuid);

    const [breadCrumbs, setBreadCrumbs] = useState<DataRoom[]>([]);
    const [rootUUID, setRootUUID] = useState<string | undefined>(undefined);
    const [list, setList] = useState<DataRoom[] | DataRoomChildren[]>([]);
    const [isDownload, setDownload] = useState(false);
    const [loading, setLoading] = useState<string | undefined>(undefined);

    useLayoutEffect(() => {
        if (stateData) {
            setBreadCrumbs(stateData.breadCrumbs);
            setList(stateData.list);
            setRootUUID(stateData.parentUUID);

            setDownload(stateData.isDownloadable);
        } else {
            if (dataFiles && dataFiles.length > 0) {
                if (dataFiles.length === 1) {
                    setRootUUID(dataFiles[0].uuid);
                }
                setList(
                    dataFiles.length === 1
                        ? dataFiles[0].type === 'Folder'
                            ? dataFiles[0].children || []
                            : dataFiles || []
                        : dataFiles,
                );
                if (dataFiles.length === 1) {
                    setDownload(dataFiles[0].isDownloadable);
                }
                setBreadCrumbs([
                    {
                        dealUUID: dataFiles[0].dealUUID,
                        group: dataFiles[0].group,
                        name:
                            dataFiles[0].group === 'DataRoom'
                                ? 'Data Room'
                                : dataFiles[0].group === 'ExecutedAgreements' || dataFiles[0].group === 'PPM'
                                ? 'Executed Agreements'
                                : 'Updates',
                        type: 'Folder',
                        uuid: '',
                        isDownloadable: dataFiles[0].isDownloadable,
                        children: dataFiles.length === 1 ? dataFiles[0].children : dataFiles,
                    },
                ]);
            }
        }
    }, [dataFiles, stateData]);

    /** Folder and file Click */
    const onClickOnFolderOrFile = (data: DataRoom) => {
        if (data.type === 'Folder') {
            let b = [...breadCrumbs];
            b.push(data);
            setBreadCrumbs(b);
            if (dataFiles.length === 1) {
                setRootUUID(dataFiles[0].uuid);
            } else if (b.length === 2) {
                setRootUUID(data.uuid);
                setDownload(data.isDownloadable);
            } else {
                setDownload(false);
            }
            setList(data.children || []);
        } else {
            if (data.name.substring(data.name.lastIndexOf('.') + 1) === 'pdf' && isDeal) {
                navigate(HEADERMENU_PATH.PDFView, {
                    state: {
                        breadCrumbs,
                        list,
                        dealUUID: data.dealUUID,
                        parentUUID:
                            (breadCrumbs || [])?.length === 1
                                ? dataFiles.length === 1
                                    ? dataFiles[0].type === 'Folder'
                                        ? data.parentUUID || dataFiles[0].uuid
                                        : data.uuid
                                    : data.uuid
                                : rootUUID || data.uuid,
                        docUUID: data.uuid,
                        fileName: data.name,
                        isDownloadable: data.isDownloadable,
                    },
                });
                return;
            }
            if (!data.investmentUUID) {
                /** Data Room File download */
                try {
                    Apis.analyticsTrackStart(data.dealUUID, data.uuid, 'DownloadDoc');
                } catch (err) {}
                setLoading(`${data.uuid}_${data.name}`);
                Apis.downloadDataRoomDocument(
                    data.dealUUID,
                    (breadCrumbs || [])?.length === 1
                        ? dataFiles.length === 1
                            ? dataFiles[0].type === 'Folder'
                                ? data.parentUUID || dataFiles[0].uuid
                                : data.uuid
                            : data.uuid
                        : rootUUID || data.uuid,
                    data.uuid,
                    data.group,
                )
                    .then((response) => {
                        try {
                            let file = new Blob([response.data], { type: response.headers['content-type'] });
                            let fileURL = window.URL.createObjectURL(file);
                            let a = document.createElement('a');
                            document.body.appendChild(a);
                            a.setAttribute('style', 'display: none');
                            a.href = fileURL;
                            a.download = data.name;
                            a.click();
                            window.URL.revokeObjectURL(fileURL);
                        } catch (err) {
                            dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                        }
                    })
                    .catch((err) => {
                        dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                    })
                    .finally(() => {
                        setLoading(undefined);
                    });
            } else {
                /** Other File download */
                setLoading(`${data.uuid}_${data.name}`);
                if (data.group !== 'PPM') {
                    Apis.downloadPaymentReceipt(
                        {
                            title: data.name,
                            documentType: 'Documents',
                        },
                        data.investmentUUID,
                    )
                        .then((response) => {
                            try {
                                let file = new Blob([response.data], { type: response.headers['content-type'] });
                                let fileURL = window.URL.createObjectURL(file);
                                let a = document.createElement('a');
                                document.body.appendChild(a);
                                a.setAttribute('style', 'display: none');
                                a.href = fileURL;
                                a.download = data.name;
                                a.click();
                                window.URL.revokeObjectURL(fileURL);
                            } catch (err) {
                                dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                            }
                        })
                        .catch((err) => {
                            dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                        })
                        .finally(() => {
                            setLoading(undefined);
                        });
                } else {
                    if (investorUUID) {
                        Apis.downloadPPM(data.dealUUID, investorUUID)
                            .then((response) => {
                                try {
                                    download(
                                        new Blob([response.data]),
                                        `Fund PPM ${data.investmentUUID}.pdf`,
                                        'application/pdf',
                                    );
                                } catch (err) {
                                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                                }
                            })
                            .catch((err) => {
                                dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                            })
                            .finally(() => {
                                setLoading(undefined);
                            });
                    }
                }
            }
        }
    };
    /** After click on BreadCrumbs */
    const breadCrumbsClick = (data: DataRoom, index: number) => {
        let b = [...breadCrumbs];
        b.splice(index + 1, b.length);
        setBreadCrumbs(b);
        if (b.length === 2) {
            setDownload(b[1].isDownloadable);
        } else {
            setDownload(false);
        }
        setList(data.children || []);
        if (index === 0) {
            if (dataFiles.length === 1) {
                setRootUUID(dataFiles[0].uuid);
                setDownload(dataFiles[0].isDownloadable);
            } else {
                setRootUUID(undefined);
            }
        }
    };

    const sortingList = useMemo(() => {
        if (list) {
            const a: DataRoom[] = list.sort((a, b) => {
                const key = 'type';
                const newSorting = {
                    key,
                    asc: false,
                };
                if (newSorting.asc) {
                    return a[key] < b[key] ? -1 : 1;
                }
                return b[key] < a[key] ? -1 : 1;
            });
            return a;
        }
        return [];
    }, [list]);

    return (
        <div>
            <BreadCrumbs
                breadCrumbs={breadCrumbs}
                breadCrumbsClick={breadCrumbsClick}
                isDownload={isDownload}
                rootUUID={rootUUID}
                isDeal={isDeal}
                setInvestmentDoc={setInvestmentDoc}
            />
            <TreeView data={sortingList} onClickOnFolderOrFile={onClickOnFolderOrFile} loader={loading} />
        </div>
    );
};

export default FolderStructure;
